class CustomHeader extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (this.initialised) {
      return;
    }

    this.bindElements();
    this.bindEvents();
    this.initialised = true;
  }

  bindEvents() {
    this.appendChild(this.announcementBar);
    this.appendChild(this.nav);
  }

  bindElements() {
    this.announcementBar = document.querySelector("main .announcement-bar");
    this.nav = document.querySelector("main .main-navigation");
  }
}
customElements.define("custom-header", CustomHeader);

// Global function to update cart count and other elements
// Global function to synchronize bundleProducts with the cart
let syncBundleProductsTimeout = null;
let syncBundleProductsInProgress = false;
let bundleBarInstance = null;
let updateProgressGlobalTimeout = null;
let updateProgressInProgress = false;

// Function for immediate update of bundleProducts based on quantity change
const updateBundleProductsImmediate = (variantId, newQuantity, oldQuantity) => {
  if (
    !window.theme?.settings?.bundleEnabled ||
    window.theme.settings.bundleCollections === null
  ) {
    return;
  }

  try {
    let eligibleProducts = JSON.parse(localStorage.getItem("bundleProducts"))
      ? JSON.parse(localStorage.getItem("bundleProducts"))
      : { tier: 0, products: [] };

    const difference = newQuantity - oldQuantity;

    if (difference > 0) {
      // Add products
      const cartContent = JSON.parse(
        sessionStorage.getItem("lastCartContent") || "{}"
      );
      const item = cartContent.items?.find(
        (item) => item.id === parseInt(variantId)
      );

      if (item) {
        for (let i = 0; i < difference; i++) {
          eligibleProducts.products.push({
            key: item.key,
            handle: item.handle,
            id: item.id,
            title: item.title,
            price: item.original_price,
            image: item.image,
          });
        }
      }
    } else if (difference < 0) {
      // Remove products
      const toRemove = Math.abs(difference);
      for (
        let i = eligibleProducts.products.length - 1;
        i >= 0 && toRemove > 0;
        i--
      ) {
        if (parseInt(eligibleProducts.products[i].id) === parseInt(variantId)) {
          eligibleProducts.products.splice(i, 1);
          toRemove--;
        }
      }
    }

    // Update tier
    const tierNumber = parseInt(window.theme.settings.bundleTotalTier);
    if (
      (eligibleProducts.products.length <= 3 && tierNumber > 1) ||
      tierNumber === 1
    ) {
      eligibleProducts.tier = 1;
    } else if (
      eligibleProducts.products.length > 3 &&
      ((eligibleProducts.products.length <= 6 && tierNumber > 2) ||
        tierNumber === 2)
    ) {
      eligibleProducts.tier = 2;
    } else if (
      eligibleProducts.products.length > 6 &&
      ((eligibleProducts.products.length <= 9 && tierNumber > 3) ||
        tierNumber === 3)
    ) {
      eligibleProducts.tier = 3;
    } else if (eligibleProducts.products.length > 9) {
      eligibleProducts.tier = 4;
    } else if (eligibleProducts.products.length === 0) {
      eligibleProducts.tier = 0;
    }

    // Save updated data
    if (eligibleProducts.products.length === 0) {
      localStorage.removeItem("bundleProducts");
    } else {
      localStorage.setItem("bundleProducts", JSON.stringify(eligibleProducts));
    }

    // Immediately update UI
    updateBundleProgressBarImmediate();
  } catch (error) {
    console.error("Error updating bundle products immediately:", error);
  }
};

// Global function for immediate update of progress bar based on local data
const updateBundleProgressBarImmediate = () => {
  if (
    !window.theme?.settings?.bundleEnabled ||
    window.theme.settings.bundleCollections === null
  ) {
    return;
  }

  try {
    if (!bundleBarInstance) {
      bundleBarInstance = document.querySelector("bundle-bar");
      if (!bundleBarInstance) {
        bundleBarInstance = new BundleBar();
      }
    }
    if (bundleBarInstance && bundleBarInstance.updateProgressInternal) {
      // Call without debounce for immediate update
      bundleBarInstance.updateProgressInternal();
    }
  } catch (error) {
    console.error("Error updating bundle progress immediately:", error);
  }
};

// Global function for updating progress bar with protection against simultaneous calls
const updateBundleProgress = () => {
  // Clear previous timer if it exists
  if (updateProgressGlobalTimeout) {
    clearTimeout(updateProgressGlobalTimeout);
    updateProgressGlobalTimeout = null;
  }

  // If update is already being executed, wait for it to complete
  if (updateProgressInProgress) {
    return;
  }

  // Execute update with debounce
  updateProgressGlobalTimeout = setTimeout(() => {
    updateProgressInProgress = true;

    try {
      if (!bundleBarInstance) {
        bundleBarInstance = document.querySelector("bundle-bar");
        if (!bundleBarInstance) {
          // If element does not exist, create a temporary instance
          bundleBarInstance = new BundleBar();
        }
      }
      if (bundleBarInstance && bundleBarInstance.updateProgressInternal) {
        bundleBarInstance.updateProgressInternal();
      }
    } finally {
      updateProgressInProgress = false;
      updateProgressGlobalTimeout = null;
    }
  }, 100); // Reduced debounce from 150ms to 100ms for faster response
};

const syncBundleProducts = async () => {
  // Clear previous timer if it exists
  if (syncBundleProductsTimeout) {
    clearTimeout(syncBundleProductsTimeout);
    syncBundleProductsTimeout = null;
  }

  // If synchronization is already being executed, wait for it to complete
  if (syncBundleProductsInProgress) {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (!syncBundleProductsInProgress) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 50);
    });
  }

  // Execute synchronization with debounce
  return new Promise((resolve) => {
    syncBundleProductsTimeout = setTimeout(async () => {
      syncBundleProductsInProgress = true;

      try {
        await syncBundleProductsInternal();
      } finally {
        syncBundleProductsInProgress = false;
        syncBundleProductsTimeout = null;
      }

      resolve();
    }, 150); // Reduced debounce from 300ms to 150ms for faster response
  });
};

const syncBundleProductsInternal = async () => {
  if (
    window.theme?.settings?.bundleEnabled &&
    window.theme.settings.bundleCollections !== null
  ) {
    // Reduced delay for faster UI updates (was 500ms)
    await new Promise((resolve) => setTimeout(resolve, 200));

    // Get the current state of the cart (with retries in case of error)
    let cartContent;
    try {
      cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      // Save last cart state for immediate UI update
      sessionStorage.setItem("lastCartContent", JSON.stringify(cartContent));
    } catch (error) {
      console.error("Error fetching cart:", error);
      // Retry after 200ms
      await new Promise((resolve) => setTimeout(resolve, 200));
      cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      sessionStorage.setItem("lastCartContent", JSON.stringify(cartContent));
    }

    // If the cart is empty, remove bundleProducts
    if (
      cartContent.item_count === 0 ||
      !cartContent.items ||
      cartContent.items.length === 0
    ) {
      localStorage.removeItem("bundleProducts");
      updateBundleProgress();
      return;
    }

    // Get the current state of bundleProducts
    let eligibleProducts = JSON.parse(localStorage.getItem("bundleProducts"))
      ? JSON.parse(localStorage.getItem("bundleProducts"))
      : { tier: 0, products: [] };

    // Create a new array of products based on the current state of the cart
    const newProducts = [];

    // For each product in the cart, add the corresponding number of records
    for (let i = 0; i < cartContent.items.length; i++) {
      const item = cartContent.items[i];
      const cartQuantity = parseInt(item.quantity);

      // Add as many records as there are products in the cart
      for (let j = 0; j < cartQuantity; j++) {
        newProducts.push({
          key: item.key,
          handle: item.handle,
          id: item.id,
          title: item.title,
          price: item.original_price,
          image: item.image,
        });
      }
    }

    // Replace the entire array of products with the new one
    eligibleProducts.products = newProducts;

    // Update tier
    const tierNumber = parseInt(window.theme.settings.bundleTotalTier);
    if (
      (eligibleProducts.products.length <= 3 && tierNumber > 1) ||
      tierNumber === 1
    ) {
      eligibleProducts.tier = 1;
    } else if (
      eligibleProducts.products.length > 3 &&
      ((eligibleProducts.products.length <= 6 && tierNumber > 2) ||
        tierNumber === 2)
    ) {
      eligibleProducts.tier = 2;
    } else if (
      eligibleProducts.products.length > 6 &&
      ((eligibleProducts.products.length <= 9 && tierNumber > 3) ||
        tierNumber === 3)
    ) {
      eligibleProducts.tier = 3;
    } else if (eligibleProducts.products.length > 9) {
      eligibleProducts.tier = 4;
    } else if (eligibleProducts.products.length === 0) {
      eligibleProducts.tier = 0;
    }

    // Save updated data
    if (eligibleProducts.products.length === 0) {
      localStorage.removeItem("bundleProducts");
    } else {
      localStorage.setItem("bundleProducts", JSON.stringify(eligibleProducts));
    }

    // Update progress bar
    updateBundleProgress();
  }
};

const renderElements = async () => {
  try {
    const cart = await fetch("/cart.js").then((r) => r.json());
    // Save cart state for immediate UI update
    sessionStorage.setItem("lastCartContent", JSON.stringify(cart));

    const getCurrency = () => window.Shopify?.currency?.active || "NZD";
    const formatPrice = (cents) => {
      const currency = getCurrency();
      const locale = currency === "AUD" ? "en-AU" : "en-NZ";
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
      }).format(cents / 100);
    };

    const summaryCount = document.querySelectorAll(
      ".cart-summary__header .cart-summary__count"
    );
    summaryCount.forEach((el) => {
      el.textContent =
        cart.item_count === 1 ? "1 item" : `${cart.item_count} items`;
    });

    const totalPriceEls = document.querySelectorAll(
      ".cart-summary__line-total p:last-child, .cart-summary__line-subtotal p:last-child"
    );
    totalPriceEls.forEach((el) => {
      el.textContent = formatPrice(cart.total_price);
    });
  } catch (error) {
    console.error("Error updating order summary", error);
  }

  const cartCountEls = document.querySelectorAll(
    "#cart-open-block .header__icons--cart-open .cart-count, .cart-count, .header__cart-count"
  );
  fetch("/cart.js")
    .then((r) => r.json())
    .then((cart) => {
      cartCountEls.forEach((el) => {
        el.textContent = cart.item_count;
        // Add/remove visually-hidden class depending on the number of products in the cart
        if (cart.item_count === 0) {
          el.classList.add("visually-hidden");
        } else {
          el.classList.remove("visually-hidden");
        }
      });
    });

  document.querySelector("#cart-open-block .icon")?.classList.add("loaded");
};

const updateCart = () => {
  fetch(window.location.pathname + "?section_id=main-cart")
    .then((res) =>
      res.text().then((e) => {
        const container = new DOMParser().parseFromString(e, "text/html");
        // upcate cart items
        const changeCartItem = async (e) => {
          const t = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              line: +e.id,
              quantity: e.quantity,
              sections: "main-cart",
            }),
          };
          return fetch(`${Shopify.routes.root}cart/change.js`, t)
            .then((response) => response.json())
            .then(async (responseData) => {
              const processResponse = async (htmlContent) => {
                const i = new DOMParser().parseFromString(
                  htmlContent,
                  "text/html"
                );
                if (0 === responseData.item_count) {
                  // if cart empty clear storage and reset cart
                  document
                    .querySelector(".cart-drawer__progress-bar")
                    ?.classList.add("cart-drawer__empty");
                  document
                    .querySelector(".free-shipping-bar")
                    ?.classList.add("cart-drawer__empty");
                  document
                    .querySelector(".cart-drawer__close-title")
                    ?.classList.add("cart-drawer__empty");
                  document
                    .querySelector(".mini-cart__footer")
                    ?.classList.add("cart-drawer__empty");
                  document.querySelector(
                    ".mini-cart__content"
                  ).innerHTML = `<div class="mini-cart--empty">
                          <p class="text-lg">Your cart is empty</p>
                          <p>There are no items in your cart.</p>
                            <a tabindex="0" href="/collections/all" class="button--primary  button--icon_left">
                            <span class="button__text">Continue shopping</span>
                          </a>
                        </div>`;
                  // Remove bundleProducts when the cart is empty
                  localStorage.removeItem("bundleProducts");
                  // Update progress bar
                  updateBundleProgress();
                  // Update cart count when cart is empty
                  await renderElements();
                } else {
                  await renderItems(i);
                  // Synchronize bundleProducts after updating the cart
                  await syncBundleProducts();
                }
              };
              if (
                responseData.sections &&
                null !== responseData.sections["main-cart"]
              ) {
                await processResponse(responseData.sections["main-cart"]);
              } else {
                const url = `${window.location.pathname}?section_id=main-cart`;
                const response = await fetch(url);
                const text = await response.text();
                await processResponse(text);
              }
              return responseData;
            })
            .catch((error) => {
              console.error(error);
              throw error;
            });
        };

        const renderItems = async (e) => {
          await renderElements();
          const t = Array.from(
              document.querySelectorAll(
                ".cart-items-container, .cart__empty, .mini-cart--empty"
              )
            ),
            i = e.querySelector(
              ".cart-items-container, .cart__empty, .mini-cart--empty"
            );
          t.forEach((e) => {
            e && e.replaceWith(i.cloneNode(!0));
          }),
            document.querySelectorAll(".cart-item").forEach((cartItem) => {
              cartItem
                .querySelectorAll(".icon")
                .forEach((icon) => icon.classList.add("loaded"));
              const t = cartItem.getAttribute("data-variant-id");
              cartItem
                .querySelector(".cart-item-delete, .cart-item__remove")
                .addEventListener("click", async () => {
                  const responseData = await changeCartItem({
                    id: t,
                    quantity: 0,
                  });
                  cartItem.remove();
                  await renderElements(); // Update cart-count after removal
                  // syncBundleProducts called in changeCartItem through renderItems
                  // Additional check for empty cart
                  if (responseData && responseData.item_count === 0) {
                    localStorage.removeItem("bundleProducts");
                    updateBundleProgress();
                  }
                });
              const i = cartItem.querySelector('input[name="quantity"]');
              if (!i) return;
              const n = cartItem.querySelector('button[name="plus"]'),
                r = cartItem.querySelector('button[name="minus"]'),
                s = cartItem.querySelector('input[name="quantity"]');
              let o = parseInt(i.value);
              n.addEventListener("click", async () => {
                const oldQuantity = o;
                (o += 1), (i.value = o);
                // Immediate UI update before synchronization with server
                updateBundleProductsImmediate(t, o, oldQuantity);
                await changeCartItem({ id: t, quantity: o });
                await renderElements();
                // syncBundleProducts called in changeCartItem through renderItems for final synchronization
              }),
                r.addEventListener("click", async () => {
                  const oldQuantity = o;
                  (o -= 1), (i.value = o);
                  // Immediate UI update before synchronization with server
                  updateBundleProductsImmediate(t, o, oldQuantity);
                  const responseData = await changeCartItem({
                    id: t,
                    quantity: o,
                  });
                  if (o === 0) {
                    cartItem.remove();
                  }
                  await renderElements();
                  // syncBundleProducts called in changeCartItem through renderItems for final synchronization
                  // Additional check for empty cart
                  if (responseData && responseData.item_count === 0) {
                    localStorage.removeItem("bundleProducts");
                    updateBundleProgressBarImmediate();
                  }
                }),
                s.addEventListener("change", async (event) => {
                  const newValue = parseInt(event.target.value);
                  if (newValue > 0) {
                    const oldQuantity = o;
                    o = newValue;
                    // Immediate UI update before synchronization with server
                    updateBundleProductsImmediate(t, o, oldQuantity);
                    await changeCartItem({ id: t, quantity: o });
                    await renderElements();
                    // syncBundleProducts called in changeCartItem through renderItems for final synchronization
                  } else if (newValue === 0) {
                    const oldQuantity = o;
                    // Immediate UI update before synchronization with server
                    updateBundleProductsImmediate(t, 0, oldQuantity);
                    const responseData = await changeCartItem({
                      id: t,
                      quantity: 0,
                    });
                    cartItem.remove();
                    await renderElements();
                    // syncBundleProducts called in changeCartItem through renderItems for final synchronization
                    // Additional check for empty cart
                    if (responseData && responseData.item_count === 0) {
                      localStorage.removeItem("bundleProducts");
                      updateBundleProgressBarImmediate();
                    }
                  } else {
                    s.value = o;
                  }
                }),
                s.addEventListener("keypress", (e) => {
                  ((8 !== e.which && 0 !== e.which && e.which < 48) ||
                    e.which > 57) &&
                    e.preventDefault();
                });
            });
        };

        renderItems(container);

        //open cart
        // const cartOverlay = document.querySelector('#cart-overlay');
        // if (!cartOverlay) {
        //   document.querySelector('body').classList.add('active', 'minicart', 'no-scroll');
        //   const overlay = document.querySelector('main').appendChild(document.createElement('div'));
        //     overlay.innerText = '&nbsp;';
        //     overlay.classList.add('overlay', 'open');
        //     overlay.setAttribute('id', 'cart-overlay');
        //   document.querySelector('drawer-component.mini-cart').classList.add('open', 'above-overlay');

        //   overlay.addEventListener('click', () => {
        //     document.querySelector('main').removeChild(document.querySelector('#cart-overlay'));
        //     document.querySelector('body').classList.remove('active', 'minicart', 'no-scroll');
        //     document.querySelector('drawer-component.mini-cart').classList.remove('open', 'above-overlay');
        //   });
        // };
        // close cart
        // const cartClose = document.querySelector('#cart-close');
        // cartClose.addEventListener('click', () => {
        //   document.querySelector('main').removeChild(document.querySelector('#cart-overlay'));
        // });
      })
    )
    .catch((res) => {
      console.error("Error updating mini cart:", res);
    });
};
class BundleCollection extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (this.initialised) {
      return;
    }

    this.bindEvents();
    this.initialised = true;
  }

  bindEvents() {
    this.atcBtns = this.querySelectorAll("[data-bundle-atc-btn]");

    // update progress bar
    this.setImages();
    this._checkCart();

    this.atcBtns.forEach((btn) => {
      if (!btn.classList.contains("active")) {
        btn.addEventListener("click", (event) => {
          event.preventDefault();
          this.toggleForm(btn.parentElement);
        });
      }
    });
  }

  toggleForm(bundleATC) {
    if (!bundleATC.classList.contains("disabled")) {
      const activeEl = bundleATC.querySelector("bundle-atc.active");

      if (activeEl) {
        activeEl.classList.remove("active");
      }
      bundleATC.classList.add("active");

      // If first time adding to cart, automatically add one to cart
      this._updateQuantity(bundleATC);
      this._onAdd(bundleATC);
    }
  }

  async _checkCart() {
    let cartItemMatchQ = 1;
    const cartContent = await (
      await fetch(`${Shopify.routes.root}cart.js`)
    ).json();

    cartContent.items.forEach((item) => {
      const currAdded = this.querySelector(
        `[data-bundle-atc][data-variant-id="${item.id}"]`
      );
      if (currAdded) {
        cartItemMatchQ += item.quantity;
        currAdded.classList.add("active");
        currAdded.querySelector(
          '.quantity-selector input[name="count"]'
        ).value = parseInt(item.quantity);

        this._updateQuantity(currAdded);
      }
    });
  }

  async _updateQuantity(currAdded) {
    const decreaseBtn = currAdded.querySelector(
      '.quantity-selector button[aria-label="Decrease quantity"'
    );
    const increaseBtn = currAdded.querySelector(
      '.quantity-selector button[aria-label="Increase quantity"'
    );

    // If click increase, it should add one product at a time.
    increaseBtn.addEventListener("click", () => {
      this._onAdd(currAdded);
    });

    // If click decrease, it should remove one product at a time.
    decreaseBtn.addEventListener("click", () => {
      this._onRemove(currAdded);
    });
  }

  async _onRemove(bundleATC) {
    let eligibleProducts = JSON.parse(localStorage.getItem("bundleProducts"))
      ? JSON.parse(localStorage.getItem("bundleProducts"))
      : { tier: 0, products: [] };
    let quantity = 0;
    this.lineKey = null;
    this.bundleATC = bundleATC;
    const cartContent = await (
      await fetch(`${Shopify.routes.root}cart.js`)
    ).json();
    const variantID = this.bundleATC.getAttribute("data-variant-id");
    const inputQuantity = this.bundleATC.querySelector(
      '.quantity-selector input[name="count"]'
    );

    for (let i = 0; i < cartContent.items.length; i++) {
      const item = cartContent.items[i];
      if (item.id === parseInt(variantID)) {
        this.lineKey = item.key;
        quantity = parseInt(item.quantity) - 1;

        break;
      }
    }

    // Update localstorage
    eligibleProducts.products.forEach((product, idx) => {
      if (product.key === this.lineKey) {
        eligibleProducts.products.splice(idx, 1);
      }
    });

    // set tier
    const tierNumber = parseInt(window.theme.settings.bundleTotalTier);
    if (
      (eligibleProducts.products.length <= 3 && tierNumber > 1) ||
      tierNumber === 1
    ) {
      eligibleProducts.tier = 1;
    } else if (
      eligibleProducts.products.length > 3 &&
      ((eligibleProducts.products.length <= 6 && tierNumber > 2) ||
        tierNumber === 2)
    ) {
      eligibleProducts.tier = 2;
    } else if (
      eligibleProducts.products.length > 6 &&
      ((eligibleProducts.products.length <= 9 && tierNumber > 3) ||
        tierNumber === 3)
    ) {
      eligibleProducts.tier = 3;
    } else if (eligibleProducts.products.length > 9) {
      eligibleProducts.tier = 4;
    } else if (eligibleProducts.products.length === 0) {
      eligibleProducts.tier = 0;
    }

    const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id: this.lineKey,
        quantity: parseInt(quantity),
      }),
    });

    const responseJson = await response.json();
    // Find .cart-messages through the parent form, since it is a sibling element of product-form__row
    const form = this.bundleATC.closest("form");
    const messageContainer = form ? form.querySelector(".cart-messages") : null;

    if (response.ok) {
      inputQuantity.value = parseInt(quantity);
      if (messageContainer) {
        messageContainer.innerHTML = `<p class="success">${quantity} in cart</p>`;
      }

      if (parseInt(quantity) === 0) {
        this.bundleATC.classList.remove("active");
      }

      // Update localstorage
      localStorage.setItem("bundleProducts", JSON.stringify(eligibleProducts));

      this.setImages();

      if (
        window.themeVariables.settings.cartType === "page" ||
        window.themeVariables.settings.pageType === "cart"
      ) {
        return (window.location.href = `${Shopify.routes.root}cart`);
      }
      const cartContent = await (
        await fetch(`${Shopify.routes.root}cart.js`)
      ).json();
      cartContent["sections"] = responseJson["sections"];

      this.dispatchEvent(
        new CustomEvent("variant:add", {
          bubbles: true,
          detail: {
            items: responseJson.hasOwnProperty("items")
              ? responseJson["items"]
              : [responseJson],
            cart: cartContent,
          },
        })
      );
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "variant:add",
            cart: cartContent,
          },
        })
      );

      await renderElements(); // Update cart-count after removal
      updateCart(); // Update cart and synchronize bundleProducts

      if (messageContainer) {
        setTimeout(() => {
          messageContainer.innerHTML = "";
        }, 3000);
      }
    } else if (response.status == 422) {
      if (messageContainer) {
        messageContainer.innerHTML = `<p class="error">${response.description}</p>`;
      }

      this.dispatchEvent(
        new CustomEvent("cart:error", {
          bubbles: true,
          detail: {
            error: responseJson["description"],
          },
        })
      );
      document.dispatchEvent(new CustomEvent("cart:refresh"));

      if (messageContainer) {
        setTimeout(() => {
          messageContainer.innerHTML = "";
        }, 3000);
      }
    }
  }

  async _onAdd(bundleATC) {
    this.bundleATC = bundleATC;
    const variantID = this.bundleATC.getAttribute("data-variant-id");
    const inputQuantity = this.bundleATC.querySelector(
      '.quantity-selector input[name="count"]'
    );

    // Find the closest form or create FormData manually
    const form = this.bundleATC.closest("form");
    const formData = form ? new FormData(form) : new FormData();

    formData.set("sections_url", `${Shopify.routes.root}variants/${variantID}`);
    formData.set("quantity", 1);

    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      body: formData,
      method: "POST",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        // Needed for Shopify to check inventory
      },
    });

    const responseJson = await response.json();
    // Find .cart-messages through the parent form, since it is a sibling element of product-form__row
    const messageContainer = form ? form.querySelector(".cart-messages") : null;

    if (response.ok) {
      inputQuantity.value = parseInt(responseJson.quantity);
      if (messageContainer) {
        messageContainer.innerHTML = `<p class="success">${responseJson.quantity} successfully added!</p>`;
      }

      this._updateProgress(bundleATC, responseJson);

      if (
        document
          .querySelector(".cart-drawer__progress-bar")
          ?.classList.contains("cart-drawer__empty")
      ) {
        document
          .querySelector(".cart-drawer__progress-bar")
          .classList.remove("cart-drawer__empty");
      }

      if (
        document
          .querySelector(".cart-drawer__close-title")
          ?.classList.contains("cart-drawer__empty")
      ) {
        document
          .querySelector(".cart-drawer__close-title")
          .classList.remove("cart-drawer__empty");
      }

      if (
        document
          .querySelector(".mini-cart__footer")
          ?.classList.contains("cart-drawer__empty")
      ) {
        document
          .querySelector(".mini-cart__footer")
          .classList.remove("cart-drawer__empty");
      }

      if (
        document
          .querySelector(".free-shipping-bar")
          ?.classList.contains("cart-drawer__empty")
      ) {
        document
          .querySelector(".free-shipping-bar")
          .classList.remove("cart-drawer__empty");
      }

      if (
        window.themeVariables.settings.cartType === "page" ||
        window.themeVariables.settings.pageType === "cart"
      ) {
        return (window.location.href = `${Shopify.routes.root}cart`);
      }

      const cartContent = await (
        await fetch(`${Shopify.routes.root}cart.js`)
      ).json();
      cartContent["sections"] = responseJson["sections"];
      this.dispatchEvent(
        new CustomEvent("variant:add", {
          bubbles: true,
          detail: {
            items: responseJson.hasOwnProperty("items")
              ? responseJson["items"]
              : [responseJson],
            cart: cartContent,
          },
        })
      );
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "variant:add",
            cart: cartContent,
          },
        })
      );
      await renderElements(); // Update cart-count immediately
      updateCart();
      document.dispatchEvent(new CustomEvent("cart:refresh"));

      if (messageContainer) {
        setTimeout(() => {
          messageContainer.innerHTML = "";
        }, 3000);
      }
    } else if (response.status == 422) {
      if (messageContainer) {
        messageContainer.innerHTML = `<p class="error">${response.description}</p>`;
      }

      this.dispatchEvent(
        new CustomEvent("cart:error", {
          bubbles: true,
          detail: {
            error: responseJson["description"],
          },
        })
      );
      document.dispatchEvent(new CustomEvent("cart:refresh"));

      if (messageContainer) {
        setTimeout(() => {
          messageContainer.innerHTML = "";
        }, 3000);
      }
    }
  }

  async _updateProgress(bundleATC, item) {
    const bundleCollections = window.theme.settings.bundleCollections;
    const collectionList =
      bundleCollections !== null ? bundleCollections.split(",") : [];
    let eligibleProducts = JSON.parse(localStorage.getItem("bundleProducts"))
      ? JSON.parse(localStorage.getItem("bundleProducts"))
      : { tier: 0, products: [] };
    this.bundleBar = this.querySelector(".collection-bundle__progress-bar");

    // on add to cart
    if (bundleCollections !== null && bundleATC) {
      // check if eligible for bundle then update local storage and progress bar
      const list = bundleATC.getAttribute("data-collection-list");
      this.list = list?.split(",");
      const eligible = this.list.filter((i) => collectionList.includes(i));

      if (eligible.length > 0 && eligible) {
        const productInfo = {
          key: item.key,
          handle: item.handle,
          id: item.id,
          title: item.title,
          price: item.original_price,
          image: item.image,
        };
        eligibleProducts.products.push(productInfo);

        const firstEmptyBox = this.bundleBar.querySelectorAll(
          '.progress-bar__box[data-activate="false"]'
        )[0];
        if (firstEmptyBox) {
          firstEmptyBox.setAttribute("data-activate", true);
          firstEmptyBox.querySelector("p").innerHTML = `
            <img src="${item.image}" alt="${item.title}" width="100" height="100%" loading="lazy" />
          `;
        }
        // set tier
        const tierNumber = parseInt(window.theme.settings.bundleTotalTier);
        if (
          (eligibleProducts.products.length <= 3 && tierNumber > 1) ||
          tierNumber === 1
        ) {
          eligibleProducts.tier = 1;
        } else if (
          eligibleProducts.products.length > 3 &&
          ((eligibleProducts.products.length <= 6 && tierNumber > 2) ||
            tierNumber === 2)
        ) {
          eligibleProducts.tier = 2;
        } else if (
          eligibleProducts.products.length > 6 &&
          ((eligibleProducts.products.length <= 9 && tierNumber > 3) ||
            tierNumber === 3)
        ) {
          eligibleProducts.tier = 3;
        } else if (eligibleProducts.products.length > 9) {
          eligibleProducts.tier = 4;
        } else if (eligibleProducts.products.length === 0) {
          eligibleProducts.tier = 0;
        }
        localStorage.setItem(
          "bundleProducts",
          JSON.stringify(eligibleProducts)
        );

        this.setPrice(eligibleProducts);
        this.updateMessaging(eligibleProducts);
        this.updateCartProgressBar();
      }
    }
  }

  setImages() {
    this.bundleBar = this.querySelector(".collection-bundle__progress-bar");
    const bundle = JSON.parse(localStorage.getItem("bundleProducts"));

    if (bundle?.products) {
      const count = bundle.products.length;
      const boxes = this.bundleBar.querySelectorAll(".progress-bar__box");

      for (let i = 0; i < boxes.length; i++) {
        const boxCount = boxes[i].getAttribute("data-count");

        if (boxCount <= count) {
          boxes[i].setAttribute("data-activate", true);
          boxes[i].querySelector("p").innerHTML = `
            <img src="${bundle.products[i].image}" alt="${bundle.products[i].title}" width="100" height="100%" loading="lazy" />
          `;
        } else {
          // On load if does not apply set to deactivate
          boxes[i].setAttribute("data-activate", false);
          if (!boxes[i].classList.contains("discount-box")) {
            boxes[i].querySelector("p").innerText = boxCount;
          } else {
            boxes[i].querySelector("p").innerHTML = "";
          }
        }
      }
      this.setPrice(bundle);
      this.updateMessaging(bundle);
    }
    this.updateCartProgressBar();
  }

  setPrice(bundleObj) {
    const priceEl = this.querySelector(
      ".collection-bundle__progress-bar .price"
    );
    const discounts =
      window.theme.settings.bundleDiscountsPercentage.split(",");
    let total = 0;
    let totalPrice = 0;

    if (discounts !== null) {
      bundleObj.products.forEach((product) => {
        total += product.price;
      });

      if (bundleObj.products.length >= 3) {
        const idx =
          bundleObj.products.length >= 12
            ? 3
            : bundleObj.products.length >= 9
            ? 2
            : bundleObj.products.length >= 6
            ? 1
            : 0;
        totalPrice = (total * (100 - discounts[idx])) / 100;
      } else {
        totalPrice = total;
      }
    }

    const price = Number.parseFloat(totalPrice / 100).toFixed(2);
    priceEl.innerHTML = `$${price}`;
  }

  updateMessaging(bundleObj) {
    const productCount = bundleObj.products.length;
    const messageEl = this.querySelector(".progress-bar__messaging .messaging");

    if (productCount !== 0) {
      const tierNumber = parseInt(window.theme.settings.bundleTotalTier);
      const boxes = this.querySelectorAll(`.progress-bar__box`).length;
      let discountAmount =
        bundleObj.tier !== 0
          ? this.querySelector(
              `.progress-bar__box.discount-box .discount-label[data-tier="${bundleObj.tier}"]`
            )?.innerText
          : 0;
      let quantityTo = 0;

      if (bundleObj.tier === 1) {
        quantityTo = productCount < 3 ? 3 - productCount : 3;
        discountAmount =
          productCount == 3
            ? this.querySelector(
                `.progress-bar__box.discount-box .discount-label[data-tier="2"]`
              )?.innerText
            : discountAmount;
      } else if (bundleObj.tier === 2) {
        quantityTo = productCount < 6 ? 6 - productCount : 3;
        discountAmount =
          productCount == 6
            ? this.querySelector(
                `.progress-bar__box.discount-box .discount-label[data-tier="3"]`
              )?.innerText
            : discountAmount;
      } else if (bundleObj.tier === 3) {
        quantityTo = productCount < 9 ? 9 - productCount : 3;
        discountAmount =
          productCount == 9
            ? this.querySelector(
                `.progress-bar__box.discount-box .discount-label[data-tier="4"]`
              )?.innerText
            : discountAmount;
      } else if (bundleObj.tier === 4) {
        quantityTo = productCount < 12 ? 12 - productCount : 3;
      }

      if (parseInt(boxes) <= productCount) {
        const discounts =
          window.theme.settings.bundleDiscountsPercentage.split(",");
        const lastDiscountI = discounts.length - 1;
        discountAmount = `${discounts[lastDiscountI]}% off`;
      }

      const message =
        bundleObj.tier === tierNumber &&
        (parseInt(boxes) < productCount || productCount === parseInt(boxes))
          ? `Congrats! You get ${discountAmount}`
          : `add ${quantityTo} more for ${discountAmount}`;
      messageEl.innerHTML = message;
    }
  }

  updateCartProgressBar() {
    let eligibleProducts = JSON.parse(localStorage.getItem("bundleProducts"))
      ? JSON.parse(localStorage.getItem("bundleProducts"))
      : { tier: 0, products: [] };
    const contentEl = document.querySelectorAll(
      ".cart-drawer__progress-bar .progress-bar__content"
    );

    contentEl.forEach((content) => {
      const tierNumber = parseInt(window.theme.settings.bundleTotalTier);
      const countEl = parseInt(content.getAttribute("data-count"));
      const tierEl = parseInt(content.getAttribute("data-tier"));
      const productCount = parseInt(eligibleProducts.products.length);

      if (eligibleProducts.tier < tierEl) {
        content
          .querySelector(".bar-fullfilled")
          ?.setAttribute("data-length", "false");
      }

      if (
        (tierNumber === 1 || (tierNumber > 1 && productCount <= 3)) &&
        productCount > 0 &&
        countEl === 3
      ) {
        if (productCount === 1) {
          content
            .querySelector(".bar-fullfilled")
            .setAttribute("data-length", "third");
        } else if (productCount === 2) {
          content
            .querySelector(".bar-fullfilled")
            .setAttribute("data-length", "half");
        } else {
          content
            .querySelector(".bar-fullfilled")
            .setAttribute("data-length", "full");
        }
      } else if (
        (tierNumber === 2 || (tierNumber > 2 && productCount <= 6)) &&
        productCount > 3 &&
        countEl === 6
      ) {
        document
          .querySelector(
            '.progress-bar__content[data-count="3"] .bar-fullfilled'
          )
          .setAttribute("data-length", "full");
        if (productCount === 4) {
          content
            .querySelector(".bar-fullfilled")
            .setAttribute("data-length", "third");
        } else if (productCount === 5) {
          content
            .querySelector(".bar-fullfilled")
            .setAttribute("data-length", "half");
        } else {
          content
            .querySelector(".bar-fullfilled")
            .setAttribute("data-length", "full");
        }
      } else if (
        (tierNumber === 3 || (tierNumber > 3 && productCount <= 9)) &&
        productCount > 6 &&
        countEl === 9
      ) {
        document
          .querySelector(
            '.progress-bar__content[data-count="3"] .bar-fullfilled'
          )
          .setAttribute("data-length", "full");
        document
          .querySelector(
            '.progress-bar__content[data-count="6"] .bar-fullfilled'
          )
          .setAttribute("data-length", "full");
        if (productCount === 7) {
          content
            .querySelector(".bar-fullfilled")
            .setAttribute("data-length", "third");
        } else if (productCount === 8) {
          content
            .querySelector(".bar-fullfilled")
            .setAttribute("data-length", "half");
        } else {
          content
            .querySelector(".bar-fullfilled")
            .setAttribute("data-length", "full");
        }
      } else if ((productCount >= 12 || productCount > 9) && countEl === 12) {
        document
          .querySelector(
            '.progress-bar__content[data-count="3"] .bar-fullfilled'
          )
          .setAttribute("data-length", "full");
        document
          .querySelector(
            '.progress-bar__content[data-count="6"] .bar-fullfilled'
          )
          .setAttribute("data-length", "full");
        document
          .querySelector(
            '.progress-bar__content[data-count="9"] .bar-fullfilled'
          )
          .setAttribute("data-length", "full");
        if (productCount === 10) {
          content
            .querySelector(".bar-fullfilled")
            .setAttribute("data-length", "third");
        } else if (productCount === 11) {
          content
            .querySelector(".bar-fullfilled")
            .setAttribute("data-length", "half");
        } else {
          content
            .querySelector(".bar-fullfilled")
            .setAttribute("data-length", "full");
        }
      }
    });
  }
}
customElements.define("bundle-collection", BundleCollection);
class BundleBtn extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initialised = true;
    // activate if not in collection bundle page
    if (!document.querySelector("bundle-collection")) {
      this.parentElement.addEventListener("submit", this._onSubmit);
    }
  }

  async _onSubmit(event) {
    this.submitting = false;
    event.preventDefault();

    if (this.submitting) {
      return;
    }
    if (!this.checkValidity()) {
      this.reportValidity();
      return;
    }
    const submitButtons = Array.from(this.elements).filter(
      (button) => button.type === "submit"
    );
    submitButtons.forEach((submitButton) => {
      submitButton.setAttribute("aria-busy", "true");
    });

    const formData = new FormData(this);
    formData.set(
      "sections_url",
      `${Shopify.routes.root}variants/${this.id.value}`
    );
    this.submitting = true;
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      body: formData,
      method: "POST",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        // Needed for Shopify to check inventory
      },
    });
    submitButtons.forEach((submitButton) => {
      submitButton.removeAttribute("aria-busy");
    });
    this.submitting = false;
    const responseJson = await response.json();
    if (response.ok) {
      if (
        document
          .querySelector(".cart-drawer__progress-bar")
          ?.classList.contains("cart-drawer__empty")
      ) {
        document
          .querySelector(".cart-drawer__progress-bar")
          .classList.remove("cart-drawer__empty");
      }

      if (
        document
          .querySelector(".cart-drawer__close-title")
          ?.classList.contains("cart-drawer__empty")
      ) {
        document
          .querySelector(".cart-drawer__close-title")
          .classList.remove("cart-drawer__empty");
      }

      if (
        document
          .querySelector(".mini-cart__footer")
          ?.classList.contains("cart-drawer__empty")
      ) {
        document
          .querySelector(".mini-cart__footer")
          .classList.remove("cart-drawer__empty");
      }

      if (
        document
          .querySelector(".free-shipping-bar")
          ?.classList.contains("cart-drawer__empty")
      ) {
        document
          .querySelector(".free-shipping-bar")
          .classList.remove("cart-drawer__empty");
      }

      const cartContent = await (
        await fetch(`${Shopify.routes.root}cart.js`)
      ).json();
      cartContent["sections"] = responseJson["sections"];
      this.dispatchEvent(
        new CustomEvent("variant:add", {
          bubbles: true,
          detail: {
            items: responseJson.hasOwnProperty("items")
              ? responseJson["items"]
              : [responseJson],
            cart: cartContent,
          },
        })
      );
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "variant:add",
            cart: cartContent,
          },
        })
      );

      // if this product belongs to a bundle product
      if (
        window.theme.settings.bundleEnabled &&
        window.theme.settings.bundleCollections !== null
      ) {
        // bundlebar
        const bundleCollections = window.theme.settings.bundleCollections;
        const collectionList =
          bundleCollections !== null ? bundleCollections.split(",") : [];
        const list = this.querySelector("[data-collection-list]").getAttribute(
          "data-collection-list"
        );
        const listArr = list?.split(",");
        const eligible = listArr?.filter((i) => collectionList.includes(i));
        let eligibleProducts = JSON.parse(
          localStorage.getItem("bundleProducts")
        )
          ? JSON.parse(localStorage.getItem("bundleProducts"))
          : { tier: 0, products: [] };

        if (eligible.length > 0 && eligible) {
          let item = responseJson;
          let productInfo = {};

          if (window.themeVariables.settings.pageType === "product") {
            const productQuantity = document.querySelector(
              '.main-product__quantity .quantity input[type="number"]'
            ).value;
            item = responseJson;

            for (let i = 0; i < productQuantity; i++) {
              if (eligible.length > 0 && eligible) {
                productInfo = {
                  key: item.key,
                  handle: item.handle,
                  id: item.id,
                  title: item.title,
                  price: item.original_price,
                  image: item.image,
                };
                eligibleProducts.products.push(productInfo);
              }
            }
          } else {
            if (eligible.length > 0 && eligible) {
              productInfo = {
                key: item.key,
                handle: item.handle,
                id: item.id,
                title: item.title,
                price: item.original_price,
                image: item.image,
              };
              eligibleProducts.products.push(productInfo);
            }
          }

          if (eligible.length > 0 && eligible) {
            // set tier
            const tierNumber = parseInt(window.theme.settings.bundleTotalTier);
            if (
              (eligibleProducts.products.length <= 3 && tierNumber > 1) ||
              tierNumber === 1
            ) {
              eligibleProducts.tier = 1;
            } else if (
              eligibleProducts.products.length > 3 &&
              ((eligibleProducts.products.length <= 6 && tierNumber > 2) ||
                tierNumber === 2)
            ) {
              eligibleProducts.tier = 2;
            } else if (
              eligibleProducts.products.length > 6 &&
              ((eligibleProducts.products.length <= 9 && tierNumber > 3) ||
                tierNumber === 3)
            ) {
              eligibleProducts.tier = 3;
            } else if (eligibleProducts.products.length > 9) {
              eligibleProducts.tier = 4;
            } else if (eligibleProducts.products.length === 0) {
              eligibleProducts.tier = 0;
            }

            localStorage.setItem(
              "bundleProducts",
              JSON.stringify(eligibleProducts)
            );

            updateBundleProgress();
          }
        }
      }

      // refresh cart and open mini cart
      await renderElements(); // Оновлюємо cart-count одразу
      updateCart();
    } else {
      this.dispatchEvent(
        new CustomEvent("cart:error", {
          bubbles: true,
          detail: {
            error: responseJson["description"],
          },
        })
      );
      document.dispatchEvent(new CustomEvent("cart:refresh"));
    }
  }
}
customElements.define("bundle-btn", BundleBtn);
class BundleBar extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (this.initialised) {
      return;
    }

    this.bindEvents();
    this.initialised = true;
  }

  bindEvents() {
    // update progress bar
    this.updateProgress();
  }

  updateProgress() {
    // Використовуємо глобальну функцію для уникнення множинних викликів
    updateBundleProgress();
  }

  updateProgressInternal() {
    if (
      window.theme.settings.bundleEnabled &&
      window.theme.settings.bundleCollections !== null
    ) {
      let eligibleProducts = JSON.parse(localStorage.getItem("bundleProducts"))
        ? JSON.parse(localStorage.getItem("bundleProducts"))
        : { tier: 0, products: [] };
      const contentEl = document.querySelectorAll(
        ".cart-drawer__progress-bar .progress-bar__content"
      );

      contentEl.forEach((content) => {
        const tierNumber = parseInt(window.theme.settings.bundleTotalTier);
        const countEl = parseInt(content.getAttribute("data-count"));
        const tierEl = parseInt(content.getAttribute("data-tier"));
        const productCount = parseInt(eligibleProducts.products.length);

        if (eligibleProducts.tier < tierEl) {
          content
            .querySelector(".bar-fullfilled")
            ?.setAttribute("data-length", "false");
        }

        if (
          (tierNumber === 1 || (tierNumber > 1 && productCount <= 3)) &&
          productCount > 0 &&
          countEl === 3
        ) {
          if (productCount === 1) {
            content
              .querySelector(".bar-fullfilled")
              .setAttribute("data-length", "third");
          } else if (productCount === 2) {
            content
              .querySelector(".bar-fullfilled")
              .setAttribute("data-length", "half");
          } else {
            content
              .querySelector(".bar-fullfilled")
              .setAttribute("data-length", "full");
          }
        } else if (
          (tierNumber === 2 || (tierNumber > 2 && productCount <= 6)) &&
          productCount > 3 &&
          countEl === 6
        ) {
          document
            .querySelector(
              '.progress-bar__content[data-count="3"] .bar-fullfilled'
            )
            .setAttribute("data-length", "full");
          if (productCount === 4) {
            content
              .querySelector(".bar-fullfilled")
              .setAttribute("data-length", "third");
          } else if (productCount === 5) {
            content
              .querySelector(".bar-fullfilled")
              .setAttribute("data-length", "half");
          } else {
            content
              .querySelector(".bar-fullfilled")
              .setAttribute("data-length", "full");
          }
        } else if (
          (tierNumber === 3 || (tierNumber > 3 && productCount <= 9)) &&
          productCount > 6 &&
          countEl === 9
        ) {
          document
            .querySelector(
              '.progress-bar__content[data-count="3"] .bar-fullfilled'
            )
            .setAttribute("data-length", "full");
          document
            .querySelector(
              '.progress-bar__content[data-count="6"] .bar-fullfilled'
            )
            .setAttribute("data-length", "full");
          if (productCount === 7) {
            content
              .querySelector(".bar-fullfilled")
              .setAttribute("data-length", "third");
          } else if (productCount === 8) {
            content
              .querySelector(".bar-fullfilled")
              .setAttribute("data-length", "half");
          } else {
            content
              .querySelector(".bar-fullfilled")
              .setAttribute("data-length", "full");
          }
        } else if ((productCount >= 12 || productCount > 9) && countEl === 12) {
          document
            .querySelector(
              '.progress-bar__content[data-count="3"] .bar-fullfilled'
            )
            .setAttribute("data-length", "full");
          document
            .querySelector(
              '.progress-bar__content[data-count="6"] .bar-fullfilled'
            )
            .setAttribute("data-length", "full");
          document
            .querySelector(
              '.progress-bar__content[data-count="9"] .bar-fullfilled'
            )
            .setAttribute("data-length", "full");
          if (productCount === 10) {
            content
              .querySelector(".bar-fullfilled")
              .setAttribute("data-length", "third");
          } else if (productCount === 11) {
            content
              .querySelector(".bar-fullfilled")
              .setAttribute("data-length", "half");
          } else {
            content
              .querySelector(".bar-fullfilled")
              .setAttribute("data-length", "full");
          }
        }
      });

      const bundleWrapper = document.querySelector(
        ".collection-bundle-container"
      );
      const progressBarContainer = document.querySelector(
        ".collection-bundle__progress-bar"
      );
      const boxes =
        progressBarContainer?.querySelectorAll(".progress-bar__box");

      if (progressBarContainer) {
        // If cart is empty
        if (eligibleProducts.products.length === 0) {
          // reset progress bar images in collection bundle page
          for (let i = 0; i < boxes.length; i++) {
            const boxCount = boxes[i].getAttribute("data-count");
            boxes[i].setAttribute("data-activate", false);
            if (!boxes[i].classList.contains("discount-box")) {
              boxes[i].querySelector("p").innerText = boxCount;
            } else {
              boxes[i].querySelector("p").innerHTML = "";
            }
          }

          // reset product quantity in collection bundle page
          const inputQuantityEls = bundleWrapper?.querySelectorAll(
            "[data-bundle-atc].active"
          );
          inputQuantityEls.forEach((el) => {
            el.classList.remove("active");
            el.querySelector(
              '.quantity-selector input[name="count"]'
            ).value = 1;
          });

          // reset total price in collection bundle page
          const priceEl = progressBarContainer?.querySelector(".price");
          priceEl.innerHTML = `$0.00`;
        } else {
          // update messaging
          const productCount = eligibleProducts.products.length;
          const messageEl = progressBarContainer.querySelector(
            ".progress-bar__messaging .messaging"
          );
          const tierNumber = parseInt(window.theme.settings.bundleTotalTier);
          let discountAmount =
            eligibleProducts.tier !== 0
              ? progressBarContainer.querySelector(
                  `.progress-bar__box.discount-box .discount-label[data-tier="${eligibleProducts.tier}"]`
                )?.innerText
              : 0;
          let quantityTo = 0;

          if (eligibleProducts.tier === 1) {
            quantityTo = productCount < 3 ? 3 - productCount : 3;
            discountAmount =
              productCount == 3
                ? progressBarContainer.querySelector(
                    `.progress-bar__box.discount-box .discount-label[data-tier="2"]`
                  )?.innerText
                : discountAmount;
          } else if (eligibleProducts.tier === 2) {
            quantityTo = productCount < 6 ? 6 - productCount : 3;
            discountAmount =
              productCount == 5
                ? progressBarContainer.querySelector(
                    `.progress-bar__box.discount-box .discount-label[data-tier="3"]`
                  )?.innerText
                : discountAmount;
          } else if (eligibleProducts.tier === 3) {
            quantityTo = productCount < 9 ? 9 - productCount : 3;
            discountAmount =
              productCount == 7
                ? progressBarContainer.querySelector(
                    `.progress-bar__box.discount-box .discount-label[data-tier="4"]`
                  )?.innerText
                : discountAmount;
          } else if (eligibleProducts.tier === 4) {
            quantityTo = productCount < 12 ? 12 - productCount : 3;
          }

          if (parseInt(boxes.length) <= productCount) {
            const discounts =
              window.theme.settings.bundleDiscountsPercentage.split(",");
            const lastDiscountI = discounts.length - 1;
            discountAmount = `${discounts[lastDiscountI]}% off`;
          }

          const message =
            eligibleProducts.tier === tierNumber &&
            (parseInt(boxes.length) < productCount ||
              productCount === parseInt(boxes.length))
              ? `Congrats! You get ${discountAmount}`
              : `add ${quantityTo} more for ${discountAmount}`;
          messageEl.innerHTML = message;
        }
      }
    }
  }
}
customElements.define("bundle-bar", BundleBar);
class CartItemQuantity extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", this._onItemUpdate);
  }

  connectedCallback() {
    if (this.initialised) {
      return;
    }
    this.initialised = true;
  }

  _onItemUpdate(event) {
    event.preventDefault();

    if (
      event.target.parentElement.tagName !== "A" ||
      !event.target.parentElement.href.includes("/cart/change")
    ) {
      if (
        !event.target.parentElement.parentElement
          .querySelector("input")
          .hasAttribute("data-line-key")
      ) {
        return;
      } else {
        const input =
          event.target.parentElement.parentElement.querySelector("input");
        this._updateBundleObj(
          input.getAttribute("data-line-key"),
          parseInt(input.value)
        ); //bundlebar
      }
    } else {
      const url = new URL(event.target.parentElement.href);
      this._updateBundleObj(url.searchParams.get("id")); //bundlebar
    }
  }

  async _updateBundleObj(lineKey, targetQuantity) {
    //bundlebar
    if (
      window.theme.settings.bundleEnabled &&
      window.theme.settings.bundleCollections !== null
    ) {
      let eligibleProducts = JSON.parse(localStorage.getItem("bundleProducts"))
        ? JSON.parse(localStorage.getItem("bundleProducts"))
        : { tier: 0, products: [] };
      let products = eligibleProducts.products;
      let updatedQ = 0;

      // Use lineKey for exact comparison, if possible
      // If lineKey contains full key (variantId:hash), use it
      // Otherwise use only variantId
      const isFullKey = lineKey.includes(":") && lineKey.split(":").length > 1;
      const affectedVariantId = lineKey.split(":")[0];

      // Update localstorage
      if (targetQuantity && targetQuantity > 0) {
        // get original product quantity - use key for exact comparison, if possible
        let originalProductQ, originalProduct;
        if (isFullKey) {
          // If we have full key, compare by key
          originalProductQ = products?.filter((p) => p.key === lineKey);
          originalProduct = products?.find((p) => p.key === lineKey);
        } else {
          // Otherwise compare by variant ID
          originalProductQ = products?.filter(
            (p) => parseInt(affectedVariantId) === parseInt(p.id)
          );
          originalProduct = products?.find(
            (p) => parseInt(affectedVariantId) === parseInt(p.id)
          );
        }

        // if the quantity change is to increase
        if (
          targetQuantity > originalProductQ.length &&
          originalProductQ.length !== 0
        ) {
          updatedQ =
            parseInt(targetQuantity) - parseInt(originalProductQ.length);
          products.push(...Array(updatedQ).fill(originalProduct));
          eligibleProducts.products = products;
        } else if (
          targetQuantity < originalProductQ.length &&
          originalProductQ.length !== 0
        ) {
          // if the quantity change is to decrease
          updatedQ = originalProductQ.length - targetQuantity;
          for (let i = products.length - 1; i >= 0 && updatedQ > 0; i--) {
            let shouldRemove = false;
            if (isFullKey) {
              shouldRemove = products[i].key === lineKey;
            } else {
              shouldRemove =
                parseInt(products[i].id) === parseInt(affectedVariantId);
            }
            if (shouldRemove) {
              products.splice(i, 1);
              updatedQ--;
            }
          }

          eligibleProducts.products = products;
        }
      } else {
        // filter through if remove - remove by key, if possible
        let keepProducts;
        if (isFullKey) {
          keepProducts = products?.filter((p) => p.key !== lineKey);
        } else {
          keepProducts = products?.filter(
            (p) => parseInt(affectedVariantId) !== parseInt(p.id)
          );
        }
        eligibleProducts.products = keepProducts;
      }

      // set tier
      const tierNumber = parseInt(window.theme.settings.bundleTotalTier);
      if (
        (eligibleProducts.products.length <= 3 && tierNumber > 1) ||
        tierNumber === 1
      ) {
        eligibleProducts.tier = 1;
      } else if (
        eligibleProducts.products.length > 3 &&
        ((eligibleProducts.products.length <= 6 && tierNumber > 2) ||
          tierNumber === 2)
      ) {
        eligibleProducts.tier = 2;
      } else if (
        eligibleProducts.products.length > 6 &&
        ((eligibleProducts.products.length <= 9 && tierNumber > 3) ||
          tierNumber === 3)
      ) {
        eligibleProducts.tier = 3;
      } else if (eligibleProducts.products.length > 9) {
        eligibleProducts.tier = 4;
      } else if (eligibleProducts.products.length === 0) {
        eligibleProducts.tier = 0;
      }

      const bundleWrapper = document.querySelector(
        ".collection-bundle-container"
      );
      const progressBarContainer = document.querySelector(
        ".collection-bundle__progress-bar"
      );
      const boxes =
        progressBarContainer?.querySelectorAll(".progress-bar__box");

      // If cart is empty, clear local storage and progress bar
      if (eligibleProducts.products.length === 0) {
        localStorage.removeItem("bundleProducts");

        if (progressBarContainer) {
          // reset progress bar images in collection bundle page
          for (let i = 0; i < boxes.length; i++) {
            const boxCount = boxes[i].getAttribute("data-count");
            boxes[i].setAttribute("data-activate", false);
            if (!boxes[i].classList.contains("discount-box")) {
              boxes[i].querySelector("p").innerText = boxCount;
            } else {
              boxes[i].querySelector("p").innerHTML = "";
            }
          }

          // reset product quantity in collection bundle page
          const inputQuantityEls = bundleWrapper?.querySelectorAll(
            "[data-bundle-atc].active"
          );
          inputQuantityEls.forEach((el) => {
            el.classList.remove("active");
            el.querySelector(
              '.quantity-selector input[name="count"]'
            ).value = 1;
          });

          // reset total price in collection bundle page
          const priceEl = progressBarContainer?.querySelector(".price");
          priceEl.innerHTML = `$0.00`;
        }
      } else {
        localStorage.setItem(
          "bundleProducts",
          JSON.stringify(eligibleProducts)
        );

        if (progressBarContainer) {
          // set images if cart is in collection bundle page
          const bundle = eligibleProducts;
          const count = bundle.products.length;

          for (let i = 0; i < boxes.length; i++) {
            const boxCount = boxes[i].getAttribute("data-count");
            if (boxCount <= count) {
              boxes[i].setAttribute("data-activate", true);
              boxes[i].querySelector("p").innerHTML = `
                <img src="${bundle.products[i].image}" alt="${bundle.products[i].title}" width="100" height="100%" loading="lazy" />
              `;
            } else {
              // On load if does not apply set to deactivate
              boxes[i].setAttribute("data-activate", false);
              if (!boxes[i].classList.contains("discount-box")) {
                boxes[i].querySelector("p").innerText = boxCount;
              } else {
                boxes[i].querySelector("p").innerHTML = "";
              }
            }
          }

          // reset product quantity in collection bundle page
          const inputQuantityEl = bundleWrapper?.querySelector(
            `[data-bundle-atc][data-variant-id="${affectedVariantId}"] .quantity-selector input[name="count"]`
          );
          if (inputQuantityEl) {
            inputQuantityEl.value = targetQuantity ? targetQuantity : 0;
            !targetQuantity
              ? bundleWrapper
                  ?.querySelector(
                    `[data-bundle-atc][data-variant-id="${affectedVariantId}"]`
                  )
                  .classList.remove("active")
              : "";
          }

          // reset total price in collection bundle page
          const priceEl = progressBarContainer?.querySelector(".price");
          const discounts =
            window.theme.settings.bundleDiscountsPercentage.split(",");
          let total = 0;
          let totalPrice = 0;

          if (discounts !== null) {
            eligibleProducts.products.forEach((product) => {
              total += product.price;
            });

            if (eligibleProducts.products.length >= 3) {
              const idx =
                eligibleProducts.products.length >= 12
                  ? 3
                  : eligibleProducts.products.length >= 9
                  ? 2
                  : eligibleProducts.products.length >= 6
                  ? 1
                  : 0;
              totalPrice = (total * (100 - discounts[idx])) / 100;
            } else {
              totalPrice = total;
            }
          }

          const price = Number.parseFloat(totalPrice / 100).toFixed(2);
          priceEl.innerHTML = `$${price}`;
        }

        // update cart progress bar
        updateBundleProgress();
      }
    }
  }
}
customElements.define("cart-item-quantity", CartItemQuantity);

class CartTemplate extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (this.initialised) {
      return;
    }

    this.bindEvents();
    this.initialised = true;
  }

  bindEvents() {
    // update progress bar
    if (window.themeVariables.settings.pageType === "cart") {
      this._checkCart();
    }
  }

  async _checkCart() {
    if (
      window.theme.settings.bundleEnabled &&
      window.theme.settings.bundleCollections !== null
    ) {
      let eligibleProducts = JSON.parse(localStorage.getItem("bundleProducts"))
        ? JSON.parse(localStorage.getItem("bundleProducts"))
        : { tier: 0, products: [] };
      let products = eligibleProducts.products;
      const cartContent = await (
        await fetch(`${Shopify.routes.root}cart.js`)
      ).json();

      // First remove products from localStorage that are not in the cart
      const cartKeys = cartContent.items.map((item) => item.key);
      eligibleProducts.products = products.filter((product) =>
        cartKeys.includes(product.key)
      );

      // Now synchronize the quantity for each product in the cart
      for (let i = 0; i < cartContent.items.length; i++) {
        const item = cartContent.items[i];
        const bundleItemCount = eligibleProducts.products.filter(
          (p) => p.key === item.key
        );
        const cartQuantity = parseInt(item.quantity);

        if (bundleItemCount.length !== cartQuantity) {
          const difference = cartQuantity - bundleItemCount.length;

          if (difference > 0) {
            // Add missing products
            for (let j = 0; j < difference; j++) {
              const productInfo = {
                key: item.key,
                handle: item.handle,
                id: item.id,
                title: item.title,
                price: item.original_price,
                image: item.image,
              };
              eligibleProducts.products.push(productInfo);
            }
          } else if (difference < 0) {
            // Remove extra products (from the end of the array)
            const toRemove = Math.abs(difference);
            for (
              let j = eligibleProducts.products.length - 1;
              j >= 0 && toRemove > 0;
              j--
            ) {
              if (eligibleProducts.products[j].key === item.key) {
                eligibleProducts.products.splice(j, 1);
                toRemove--;
              }
            }
          }
        }
      }

      // Update tier
      const tierNumber = parseInt(window.theme.settings.bundleTotalTier);
      if (
        (eligibleProducts.products.length <= 3 && tierNumber > 1) ||
        tierNumber === 1
      ) {
        eligibleProducts.tier = 1;
      } else if (
        eligibleProducts.products.length > 3 &&
        ((eligibleProducts.products.length <= 6 && tierNumber > 2) ||
          tierNumber === 2)
      ) {
        eligibleProducts.tier = 2;
      } else if (
        eligibleProducts.products.length > 6 &&
        ((eligibleProducts.products.length <= 9 && tierNumber > 3) ||
          tierNumber === 3)
      ) {
        eligibleProducts.tier = 3;
      } else if (eligibleProducts.products.length > 9) {
        eligibleProducts.tier = 4;
      } else if (eligibleProducts.products.length === 0) {
        eligibleProducts.tier = 0;
      }

      // Save updated data
      if (eligibleProducts.products.length === 0) {
        localStorage.removeItem("bundleProducts");
      } else {
        localStorage.setItem(
          "bundleProducts",
          JSON.stringify(eligibleProducts)
        );
      }

      // Update progress bar
      updateBundleProgress();
    }
  }
}
customElements.define("cart-template", CartTemplate);

function updateShippingBar() {
  const cartTotalEl = document.querySelector("#cart-total-price");
  const cartOriginalEl = document.querySelector("#cart-original-total-price");
  const barFill = document.querySelector(".free-shipping-bar__fill");
  const barText = document.querySelector(".free-shipping-bar p");

  const freeShippingThreshold =
    window.shopSettings?.thresholdFreeShipping || 80; // fallback 80

  if (!cartTotalEl || !barFill || !barText) return;

  const cartTotal = parseFloat(cartTotalEl.textContent.replace(/[^0-9.]/g, ""));

  let cartOriginalTotal = 0;
  if (cartOriginalEl) {
    cartOriginalTotal = parseFloat(
      cartOriginalEl.textContent.replace(/[^0-9.]/g, "")
    );
  }

  const progressPercent = Math.min(
    (cartTotal / freeShippingThreshold) * 100,
    100
  );
  const remaining = Math.max(freeShippingThreshold - cartTotal, 0);

  barFill.style.width = progressPercent + "%";
  barText.textContent =
    cartTotal >= freeShippingThreshold
      ? "You have qualified for free shipping"
      : `You are $${remaining.toFixed(2)} away from free shipping!`;
}

// Product quantity selector - uses event delegation so clicks work even with dynamic content
let productQuantitySelectorInitialized = false;
function initProductQuantitySelector() {
  if (productQuantitySelectorInitialized) return;
  productQuantitySelectorInitialized = true;

  document.addEventListener(
    "click",
    (e) => {
      const plusBtn = e.target.closest('.quantity button[name="plus"]');
      const minusBtn = e.target.closest('.quantity button[name="minus"]');
      if (!plusBtn && !minusBtn) return;

      const quantityWrapper = (plusBtn || minusBtn).closest(".quantity");
      if (!quantityWrapper) return;

      // Skip cart quantity (handled by CartItemQuantity)
      if (quantityWrapper.closest("cart-item-quantity")) return;

      // Skip bundle quantity (handled by BundleBtn - uses input[name="count"])
      if (quantityWrapper.classList.contains("quantity-selector") || quantityWrapper.closest(".bundle-quantity")) return;

      const quantityInput = quantityWrapper.querySelector('input[name="quantity"]');
      if (!quantityInput) return;

      e.preventDefault();
      e.stopPropagation();

      const form = quantityInput.form || quantityInput.closest("form");
      const hiddenInput = form?.querySelector('input[name="quantity"][type="hidden"]') || quantityWrapper.closest(".main-product__content, .sticky-add-to-cart")?.querySelector("#quantity");

      let quantity = parseInt(quantityInput.value) || 1;
      if (plusBtn) quantity += 1;
      else if (minusBtn && quantity > 1) quantity -= 1;

      quantity = Math.max(1, quantity);
      quantityInput.value = quantity;
      if (hiddenInput) hiddenInput.value = quantity;

      // Sync with sticky add-to-cart
      const stickyAddToCart = document.querySelector(".sticky-add-to-cart");
      if (stickyAddToCart) {
        const stickyQty = stickyAddToCart.querySelector('.quantity input[name="quantity"]');
        const stickyHidden = stickyAddToCart.querySelector('input[name="quantity"][type="hidden"]');
        if (stickyQty) stickyQty.value = quantity;
        if (stickyHidden) stickyHidden.value = quantity;
      }
      const mainContent = document.querySelector(".main-product__content");
      if (mainContent && !quantityWrapper.closest(".main-product__content")) {
        const mainQty = mainContent.querySelector('.quantity input[name="quantity"]');
        const mainHidden = mainContent.querySelector('input[name="quantity"][type="hidden"]');
        if (mainQty) mainQty.value = quantity;
        if (mainHidden) mainHidden.value = quantity;
      }
    },
    true
  );

  // Input change and keypress - attach per input when found
  document.addEventListener("change", (e) => {
    if (e.target.matches('.quantity input[name="quantity"]') && !e.target.closest("cart-item-quantity")) {
      const val = parseInt(e.target.value);
      if (val > 0) {
        const quantityWrapper = e.target.closest(".quantity");
        const form = e.target.form || e.target.closest("form");
        const hiddenInput = form?.querySelector('input[name="quantity"][type="hidden"]');
        if (hiddenInput) hiddenInput.value = val;
        const stickyAddToCart = document.querySelector(".sticky-add-to-cart");
        if (stickyAddToCart) {
          const stickyQty = stickyAddToCart.querySelector('.quantity input[name="quantity"]');
          const stickyHidden = stickyAddToCart.querySelector('input[name="quantity"][type="hidden"]');
          if (stickyQty) stickyQty.value = val;
          if (stickyHidden) stickyHidden.value = val;
        }
      }
    }
  });

  document.addEventListener("keypress", (e) => {
    if (e.target.matches('.quantity input[name="quantity"]') && !e.target.closest("cart-item-quantity")) {
      if (e.which !== 8 && e.which !== 0 && (e.which < 48 || e.which > 57)) e.preventDefault();
    }
  });
}

document.addEventListener("DOMContentLoaded", () => {
  initProductQuantitySelector();
  updateShippingBar();

  const cartContent = document.querySelector(".mini-cart__content");
  if (cartContent) {
    const observer = new MutationObserver(() => {
      updateShippingBar();
    });
    observer.observe(cartContent, { childList: true, subtree: true });
  }

  // Synchronize bundleProducts when page loads
  syncBundleProducts();
});

// Event listeners for automatic synchronization of bundleProducts
document.addEventListener("cart:change", () => {
  syncBundleProducts();
});

document.addEventListener("cart:refresh", () => {
  syncBundleProducts();
});

// Additional check when localStorage changes (in case of external changes)
window.addEventListener("storage", (e) => {
  if (e.key === "bundleProducts") {
    syncBundleProducts();
  }
});

// Periodic check for synchronization (every 2 seconds if there are bundleProducts)
let syncCheckInterval = null;
const startSyncCheck = () => {
  if (syncCheckInterval) return;
  syncCheckInterval = setInterval(async () => {
    const bundleProducts = localStorage.getItem("bundleProducts");
    if (bundleProducts) {
      const cart = await fetch(`${Shopify.routes.root}cart.js`).then((r) =>
        r.json()
      );
      if (cart.item_count === 0) {
        localStorage.removeItem("bundleProducts");
        updateBundleProgress();
        if (syncCheckInterval) {
          clearInterval(syncCheckInterval);
          syncCheckInterval = null;
        }
      } else {
        // Check if the number of products matches
        const bundle = JSON.parse(bundleProducts);
        const totalCartItems = cart.items.reduce(
          (sum, item) => sum + item.quantity,
          0
        );
        if (bundle.products.length !== totalCartItems) {
          syncBundleProducts();
        }
      }
    } else {
      if (syncCheckInterval) {
        clearInterval(syncCheckInterval);
        syncCheckInterval = null;
      }
    }
  }, 2000);
};

// Start check when page loads
document.addEventListener("DOMContentLoaded", () => {
  startSyncCheck();
});

// Update progress bar when returning to page via browser back button
window.addEventListener("pageshow", (event) => {
  // event.persisted is true when page is loaded from cache (back/forward navigation)
  if (event.persisted) {
    // Small delay to ensure page is fully loaded
    setTimeout(async () => {
      // Update cart count and other elements
      await renderElements();
      // Synchronize bundleProducts
      await syncBundleProducts();
      // Also update bundle collection progress bar if on bundle collection page
      const bundleCollection = document.querySelector("bundle-collection");
      if (bundleCollection && bundleCollection.setImages) {
        bundleCollection.setImages();
      }
      // Update bundle bar progress
      const bundleBar = document.querySelector("bundle-bar");
      if (bundleBar && bundleBar.updateProgress) {
        bundleBar.updateProgress();
      }
      // Update shipping bar
      updateShippingBar();
    }, 200);
  }
});

// Update progress bar when window regains focus (user returns to tab)
let lastFocusTime = 0;
window.addEventListener("focus", () => {
  const now = Date.now();
  // Throttle to avoid too frequent updates (max once per 500ms)
  if (now - lastFocusTime > 500) {
    lastFocusTime = now;
    setTimeout(async () => {
      await renderElements();
      await syncBundleProducts();
      const bundleCollection = document.querySelector("bundle-collection");
      if (bundleCollection && bundleCollection.setImages) {
        bundleCollection.setImages();
      }
      const bundleBar = document.querySelector("bundle-bar");
      if (bundleBar && bundleBar.updateProgress) {
        bundleBar.updateProgress();
      }
      updateShippingBar();
    }, 200);
  }
});

const titles = document.querySelectorAll(
  "drawer-component .minicart-slider__title svg"
);

titles.forEach((svg) => {
  svg.addEventListener("click", () => {
    svg.classList.toggle("minicart-slider__svg-active");
    const slider = document.querySelectorAll(
      "drawer-component .minicart-slider__slider"
    )[0];
    if (slider) {
      slider.classList.toggle("minicart-slider__slider-hide");
    }
  });
});

// Move Vtl-VolumeDiscountRadio__TierHighlight into Vtl-VolumeDiscountRadio__LabelHeader
function moveTierHighlightToLabelHeader() {
  const tierHighlights = document.querySelectorAll('.Vtl-VolumeDiscountRadio__TierHighlight');

  tierHighlights.forEach((tierHighlight) => {
    const labelWrapper = tierHighlight.closest('.Vtl-VolumeDiscountRadio__LabelWrapper');

    if (labelWrapper) {
      const labelHeader = labelWrapper.querySelector('.Vtl-VolumeDiscountRadio__LabelHeader');

      if (labelHeader && !labelHeader.contains(tierHighlight)) {
        labelHeader.appendChild(tierHighlight);
      }
    }
  });
}

// Run immediately when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', moveTierHighlightToLabelHeader);
} else {
  moveTierHighlightToLabelHeader();
}

// Also run when new content is added (for dynamic content)
const volumeDiscountObserver = new MutationObserver(() => {
  moveTierHighlightToLabelHeader();
});

volumeDiscountObserver.observe(document.body, {
  childList: true,
  subtree: true
});
